<template>
  <div class="flex flex-row flex-nowrap" :class="`gap-${gap}`">
    <div class="basis-5/12">
      <bvc-select
        name="service-duration-hours"
        :class="$style.select"
        :label="$t('hours')"
        :options="intervalOptions.hours"
        v-model="duration.hours"
        :height="height"
        :position="position"
        :minWidth="minWidth"
        :disabled="disabled"
        :searchable="false"
      />
    </div>
    <div class="basis-6/12 flex-grow">
      <bvc-select
        name="service-duration-minutes"
        :class="$style.select"
        :label="$t('minutes')"
        :options="intervalOptions.minutes"
        v-model="duration.minutes"
        :height="height"
        :position="position"
        :minWidth="minWidth"
        :disabled="disabled"
        :searchable="false"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
defineOptions({
  name: 'components-common-b-select-duration',
})

import { ref, computed, watch, type PropType } from 'vue'
import { useVModel } from '@vueuse/core'
import moment from 'moment'

import BvcSelect, { type SelectOption } from '@/booksy-vue-components/components/b-select/index.vue'

const props = defineProps({
  modelValue: {
    type: Number, // minutes
  },

  minutesOptions: {
    type: Array as PropType<SelectOption[]>,
  },

  hoursOptions: {
    type: Array as PropType<SelectOption[]>,
  },

  formatHoursLabel: {
    type: Function,
  },

  formatMinutesLabel: {
    type: Function,
  },

  height: {
    type: [Number, String],
    default: 'auto',
  },

  position: {
    type: String,
    default: 'bottom-left',
  },

  minWidth: {
    type: Number,
    default: 0,
  },

  disabled: {
    type: Boolean,
    default: false,
  },

  gap: {
    type: Number,
    default: 6,
  },
})

const emit = defineEmits(['update:modelValue', 'change'])

const modelValue = useVModel(props, 'modelValue', emit)

const duration = ref({
  minutes: null,
  hours: null,
})

const getFormattedMinutesLabel = (v) => {
  return typeof props.formatMinutesLabel === 'function'
    ? props.formatMinutesLabel(v)
    : `${v} minute(s)`
}

const getFormattedHoursLabel = (v) => {
  return typeof props.formatHoursLabel === 'function'
    ? props.formatHoursLabel(v)
    : `${v} hour(s)`
}

const intervalOptions = computed((): { hours: SelectOption[]; minutes: SelectOption[] } => {
  const hours: SelectOption[] = [...(props.hoursOptions ?? [])]
  const minutes: SelectOption[] = [...(props.minutesOptions ?? [])]

  if (!props.minutesOptions) {
    for (let min = 0; min < 59; min += 5) {
      minutes.push({
        value: min,
        label: getFormattedMinutesLabel(min),
      })
    }
  }

  if (!props.hoursOptions) {
    for (let hour = 0; hour < 24; hour += 1) {
      hours.push({
        value: hour,
        label: getFormattedHoursLabel(hour),
      })
    }
  }

  return {
    minutes,
    hours,
  }
})

watch(
  () => props.modelValue,
  (newValue) => {
    const momentDuration = moment.duration(newValue, 'minutes')

    duration.value = {
      minutes: momentDuration.minutes(),
      hours: momentDuration.hours(),
    }
  },
  { immediate: true }
)

watch(
  duration,
  () => {
    const momentDuration = moment.duration(duration.value.hours, 'hours')
    momentDuration.add(duration.value.minutes, 'minutes')

    modelValue.value = momentDuration.as('minutes')
    emit('change')
  },
  { deep: true }
)
</script>

<style lang="scss" module>
@layer legacy-views {

.select {
  &:last-of-type {
    margin-right: 0;
  }
}

}</style>
